// A window of time over the deal NUMBERS.
//
// The distinction this module exists to keep: a lens narrows the LIST, a period
// narrows the ARITHMETIC. Nothing here ever removes a deal from a view. Old deals
// stay on the page and stay findable; what a period decides is which of them the
// two totals at the top and the stage bar under them are adding up.
//
// Which date a deal is measured by is the stage's business, not the period's, and
// it is the one non-obvious thing in here. A deal carries up to three dates and the
// stage says which one has actually happened: an open deal is only ever a forecast,
// so it lands in the quarter it is expected to close; a signed one is a fact, so it
// lands in the quarter it was signed. That is what makes one control answer both
// "what could land this quarter" and "what did".

import { todayIso } from './format'
import { type Stage } from './model'
import { fieldStr, stageOf, type EntityRecord } from './reducer'

export interface Period {
  /** stable and parseable, because this is what the device remembers */
  key: string
  label: string
  /** inclusive ISO bounds. `null` on both is all time, which is the default. */
  from: string | null
  to: string | null
}

export const ALL_TIME: Period = { key: 'all', label: 'All time', from: null, to: null }

const pad = (n: number) => String(n).padStart(2, '0')
/** Day 0 of the next month is the last day of this one, leap years included. */
const lastDayOf = (year: number, month: number) => new Date(Date.UTC(year, month, 0)).getUTCDate()

export function quarterPeriod(year: number, quarter: number): Period {
  const first = (quarter - 1) * 3 + 1
  const last = first + 2
  return {
    key: `q${year}-${quarter}`,
    label: `Q${quarter} ${year}`,
    from: `${year}-${pad(first)}-01`,
    to: `${year}-${pad(last)}-${pad(lastDayOf(year, last))}`,
  }
}

export function yearPeriod(year: number): Period {
  return { key: `y${year}`, label: String(year), from: `${year}-01-01`, to: `${year}-12-31` }
}

export function quarterOf(iso: string): { year: number; quarter: number } {
  return {
    year: Number(iso.slice(0, 4)),
    quarter: Math.floor((Number(iso.slice(5, 7)) - 1) / 3) + 1,
  }
}

/** Back from a remembered key. Unknown keys fall through to all time rather than
 *  throwing: a stored preference is not trusted input. */
export function parsePeriod(key: string): Period | null {
  if (key === ALL_TIME.key) return ALL_TIME
  const q = /^q(\d{4})-([1-4])$/.exec(key)
  if (q) return quarterPeriod(Number(q[1]), Number(q[2]))
  const y = /^y(\d{4})$/.exec(key)
  if (y) return yearPeriod(Number(y[1]))
  return null
}

/**
 * The four that earn a chip. Relabelled on purpose — "This quarter" is what you
 * mean four times a day, and it keeps meaning it in January without anyone
 * re-picking — but they keep the underlying key, so what gets remembered is the
 * quarter, not the phrase.
 */
export function presetPeriods(today = todayIso()): Period[] {
  const { year, quarter } = quarterOf(today)
  return [
    ALL_TIME,
    { ...quarterPeriod(year, quarter), label: 'This quarter' },
    { ...yearPeriod(year), label: 'This year' },
    yearPeriod(year - 1),
  ]
}

/**
 * Everything else, for the menu behind the chips: each year the deals reach, and
 * under it only the quarters that have a deal in them. An empty quarter is a menu
 * entry that can only ever answer zero, and the whole list would otherwise grow by
 * four every year whether or not anything happened.
 *
 * The presets are excluded, so the two controls never both light up for the same
 * period and "which one am I on" has a single answer.
 */
export function periodChoices(dates: (string | null)[], today = todayIso()): Period[] {
  const seen = new Set<string>()
  for (const iso of dates) {
    if (!iso) continue
    const { year, quarter } = quarterOf(iso)
    seen.add(`${year}-${quarter}`)
  }
  const presets = new Set(presetPeriods(today).map((p) => p.key))
  const years = [...new Set([...seen].map((k) => Number(k.split('-')[0])))].sort((a, b) => b - a)

  const out: Period[] = []
  for (const year of years) {
    const y = yearPeriod(year)
    if (!presets.has(y.key)) out.push(y)
    for (const quarter of [4, 3, 2, 1]) {
      if (!seen.has(`${year}-${quarter}`)) continue
      const q = quarterPeriod(year, quarter)
      if (!presets.has(q.key)) out.push(q)
    }
  }
  return out
}

/**
 * The date a deal's stage has made real: a forecast while it is open, the signature
 * once there is one. A deal that has neither has no date at all — which is most of
 * them early on, and why every caller has to say how many it left out rather than
 * quietly shrinking a total.
 */
export function dealDate(deal: EntityRecord, stages: Stage[]): string | null {
  const kind = stages.find((s) => s.key === stageOf(deal, stages))?.kind
  const signed = fieldStr(deal, 'signedAt')
  const expected = fieldStr(deal, 'expectedClose')
  // Signed, running or delivered: the signature is the fact. The forecast it beat
  // is still the fallback, for deals signed before the field existed.
  if (kind === 'won' || kind === 'delivery' || kind === 'closed') return signed || expected || null
  return expected || null
}

/** All time takes everything, a dated period takes only what is dated. */
export function inPeriod(iso: string | null, period: Period): boolean {
  if (!period.from || !period.to) return true
  if (!iso) return false
  return iso >= period.from && iso <= period.to
}
